import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import ProgressDialog from "@Components/game/progress/progress-dialog";
import formatMetricScoreType from "@Containers/app/stat-cards/tooltips/format-metric-type";
import ProgressChart from "@Containers/shared/cards/progress-chart";
import { calculateAveragePercentageChange } from "@Containers/shared/helpers";
import { getLatestDefinedScore } from "@Containers/shared/progress/utils";
import { AppContext } from "@Context/common/general/context";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { getPerformanceColor } from "@Helpers/feedback";
import useI18n from "@i18n";
import { Stack, Typography, useTheme } from "@mui/material";
import { useContext, useState } from "react";

interface IProgressMetricCard {
  metricKey: string;
  metricScores: Array<{ name: string; score: number | null }>;
  isLoading?: boolean;
}

const sliceToLastDefinedScores = <T extends { score: number | null }>(scores: T[], maxDefinedScores: number): T[] => {
  if (scores.length === 0) return scores;

  const definedIndices: number[] = [];
  scores.forEach((item, index) => {
    if (item.score !== null && item.score !== undefined) {
      definedIndices.push(index);
    }
  });

  if (definedIndices.length <= maxDefinedScores) {
    return scores;
  }

  const startIndex = definedIndices[definedIndices.length - maxDefinedScores];
  return scores.slice(startIndex);
};

const ProgressMetricCard = ({ metricKey, metricScores, isLoading = false }: IProgressMetricCard) => {
  const { i18n } = useI18n();
  const { state } = useContext(AppContext);
  const theme = useTheme();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentMetricKey, setCurrentMetricKey] = useState(metricKey);

  const handleOpenDialog = () => {
    setCurrentMetricKey(metricKey);
    setIsDialogOpen(true);
  };

  const chartScores = sliceToLastDefinedScores(metricScores, 5);
  const currentScore = getLatestDefinedScore(metricScores) ?? 0;

  const percentageChange = calculateAveragePercentageChange(metricScores);
  const isPositive = percentageChange > 0;
  const percentageChangeColor =
    percentageChange === 0 ? theme.palette.dsNeutral.v400 : isPositive ? theme.palette.dsGreen?.v200 : theme.palette.dsRed?.v200;

  const formattedScore = Number(formatMetricScoreType(metricKey, currentScore));
  const creationDate = state.user.creationDate ? new Date(state.user.creationDate).getTime() : null;

  const chartColor = getPerformanceColor(metricKey as MetricFilterTypes, formattedScore || 0, theme, creationDate);
  const chartHeight = parseInt(theme.spacing(17.5));

  return (
    <>
      <BybVrCard onClick={handleOpenDialog} sx={{ paddingBottom: theme.spacing(3) }}>
        <Stack spacing={2} width="100%" alignItems="stretch">
          <Typography variant="text1xl500" color="text.secondary" textAlign="left">
            {i18n(`metric.${metricKey}.name`)}
          </Typography>

          <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
            <Stack direction="row" alignItems="baseline" spacing={0.5} flex={1} minWidth={0} overflow="hidden">
              <Typography variant="text3xl700" color="text.primary">
                {formattedScore}
              </Typography>
              <Typography variant="text1xl500" color="text.secondary" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {i18n(`metric.${metricKey}.short`)}
              </Typography>
            </Stack>
            <Typography variant="textLg400" color={percentageChangeColor}>
              {isPositive ? "+" : ""}
              {percentageChange}%
            </Typography>
          </Stack>

          <ProgressChart data={chartScores} color={chartColor} height={chartHeight} hideXAxis chartId={metricKey} showAverageScore showUnit />
        </Stack>
      </BybVrCard>

      <ProgressDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        currentMetricKey={currentMetricKey as MetricFilterTypes}
        onMetricChange={key => setCurrentMetricKey(key)}
      />
    </>
  );
};

export default ProgressMetricCard;
